import { useState, useEffect } from "react";
import { Box, Chip, Stack } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import { useNavigate } from "react-router";
import { request } from "../../config/request.ts";
import DataTable, { Column } from "../../shared/DataTable/index.tsx";
import PageHeader from "../../shared/PageHeader/index.tsx";
import DeleteConfirmation from "../../shared/DeleteConfirmation/index.tsx";
import StatusChip from "../../shared/StatusChip/index.tsx";
import ActionIcons from "../../shared/ActionIcons/index.tsx";
import PageWrapper from "../../shared/PageWrapper/index.tsx";

interface Honour {
  id: number;
  name: string;
  season: string | null;
  results: string[];
  sort_order: number;
  status: boolean;
}

export default function Honours() {
  const navigate = useNavigate();
  const [honours, setHonours] = useState<Honour[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(25);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    getHonours();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, limit, search]);

  const getHonours = async () => {
    setLoading(true);
    try {
      const response = await request.post("honours", {
        search: search,
        limit,
        offset: page,
        orderColumn: "sort_order",
        orderDirection: "desc",
      });
      setHonours(response.data.data);
      setTotalCount(response.data.total);
    } finally {
      setLoading(false);
    }
  };

  const handlePaginationChange = (newPage: number, newLimit: number) => {
    setPage(newPage);
    setLimit(newLimit);
  };

  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    id: 0,
    title: null as string | null,
  });

  const columns: Column[] = [
    {
      id: "slNo",
      label: "Sl.No",
      minWidth: 60,
      format: (_value, _row, index) => (
        <Box component="span" sx={{ fontWeight: 600, color: "#64748b" }}>
          {page * limit + index + 1}
        </Box>
      ),
    },
    {
      id: "name",
      label: "Name",
      minWidth: 220,
      format: (value) => value,
    },
    {
      id: "season",
      label: "Season",
      minWidth: 130,
      format: (value) => value || "—",
    },
    {
      id: "results",
      label: "Results",
      minWidth: 280,
      format: (value: string[]) => (
        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
          {(value || []).slice(0, 2).map((result) => (
            <Chip key={result} label={result} size="small" />
          ))}
          {(value || []).length > 2 && (
            <Chip
              label={`+${value.length - 2}`}
              size="small"
              color="primary"
            />
          )}
          {!value?.length && "—"}
        </Stack>
      ),
    },
    {
      id: "sort_order",
      label: "Sort Order",
      minWidth: 110,
      format: (value) => value,
    },
    {
      id: "status",
      label: "Status",
      minWidth: 100,
      align: "center",
      format: (value) => <StatusChip active={value === true} />,
    },
    {
      id: "actions",
      label: "Actions",
      minWidth: 120,
      align: "right",
      format: (_value, row) => (
        <ActionIcons
          actions={[
            {
              icon: <EditIcon />,
              tooltip: "Edit",
              onClick: () => navigate(`/admin/honours/${row.id}`),
            },
            {
              icon: <DeleteIcon />,
              isDelete: true,
              tooltip: "Delete",
              onClick: () =>
                setDeleteDialog({
                  open: true,
                  id: row.id,
                  title: row?.name,
                }),
            },
          ]}
        />
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title="Honours"
        showSearch
        searchPlaceholder="Search honours..."
        onSearchChange={(value) => setSearch(value)}
        defaultSearchValue={search}
        primaryButton={{
          label: "Add Honour",
          icon: <AddIcon />,
          onClick: () => {
            navigate("/admin/honours/create");
          },
        }}
      />
      <PageWrapper loading={loading}>
        <DataTable
          columns={columns}
          rows={honours}
          totalCount={totalCount}
          onPaginationChange={handlePaginationChange}
          page={page}
          limit={limit}
          tableName="honours"
          sortField="sort_order"
          onSortOrderChange={getHonours}
        />
        <DeleteConfirmation
          open={deleteDialog.open}
          title={"Delete Honour - " + deleteDialog?.title}
          message="Are you sure you want to delete this honour? This action cannot be undone."
          apiEndpoint={"honours/"}
          deleteId={deleteDialog.id}
          onCancel={() => setDeleteDialog({ open: false, id: 0, title: null })}
          refetchApi={() => {
            setPage(0);
            getHonours();
          }}
        />
      </PageWrapper>
    </>
  );
}
